import { DollarSign, MapPin, Phone, Mail, Clock, Shield, Users, CheckCircle, Star, Award, CreditCard, Zap, Building, FileText, Calculator, TrendingUp, BookOpen, HelpCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-100">
      {/* Header */}
      <header className="bg-white shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-4">
              <img 
                src="https://quickloanpro.com/storage/2023/11/Payday-Loans-Louisiana-Logo.png" 
                alt="QuickLoanPro Logo"
                className="w-16 h-16 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">QuickLoanPro</h1>
                <p className="text-gray-600">Your Trusted Financial Partner</p>
              </div>
            </Link>
            <div className="hidden md:flex items-center gap-4">
              <Phone className="w-5 h-5 text-green-600" />
              <span className="text-gray-700">+1 (504) 438-1753</span>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-blue-600 text-white py-3">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-6">
            <Link to="/" className="hover:text-blue-200 transition-colors">Home</Link>
            <Link to="/about" className="hover:text-blue-200 transition-colors">About Us</Link>
            <Link to="/contact" className="hover:text-blue-200 transition-colors">Contact</Link>
            <Link to="/privacy" className="font-semibold">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-blue-200 transition-colors">Terms of Service</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-green-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Privacy Policy
          </h2>
          <p className="text-xl md:text-2xl mb-8 opacity-90 max-w-3xl mx-auto">
            Your privacy is important to us. Learn how QuickLoanPro collects, uses, and protects your personal information.
          </p>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="p-8">
              <CardContent className="prose max-w-none">
                <p className="text-sm text-gray-600 mb-8">
                  <strong>Effective Date:</strong> January 1, 2025<br />
                  <strong>Last Updated:</strong> January 1, 2025
                </p>

                <h3 className="text-2xl font-bold mb-4 text-gray-900">1. Information We Collect</h3>
                <p className="mb-4 text-gray-700">
                  We collect information you provide directly to us, such as when you create an account, apply for a loan, or contact us for support. This may include:
                </p>
                <ul className="list-disc list-inside mb-6 text-gray-700 space-y-2">
                  <li>Personal identification information (name, address, phone number, email)</li>
                  <li>Financial information (income, employment, banking details)</li>
                  <li>Government-issued identification numbers</li>
                  <li>Credit and financial history information</li>
                </ul>

                <h3 className="text-2xl font-bold mb-4 text-gray-900">2. How We Use Your Information</h3>
                <p className="mb-4 text-gray-700">
                  We use the information we collect to:
                </p>
                <ul className="list-disc list-inside mb-6 text-gray-700 space-y-2">
                  <li>Process and evaluate loan applications</li>
                  <li>Verify your identity and prevent fraud</li>
                  <li>Communicate with you about your account and services</li>
                  <li>Comply with legal and regulatory requirements</li>
                  <li>Improve our services and customer experience</li>
                </ul>

                <h3 className="text-2xl font-bold mb-4 text-gray-900">3. Information Sharing</h3>
                <p className="mb-4 text-gray-700">
                  We may share your information with:
                </p>
                <ul className="list-disc list-inside mb-6 text-gray-700 space-y-2">
                  <li>Service providers who assist us in operating our business</li>
                  <li>Credit reporting agencies and verification services</li>
                  <li>Legal authorities when required by law</li>
                  <li>Business partners for loan servicing and collection</li>
                </ul>

                <h3 className="text-2xl font-bold mb-4 text-gray-900">4. Data Security</h3>
                <p className="mb-6 text-gray-700">
                  We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes encryption, secure servers, and regular security assessments.
                </p>

                <h3 className="text-2xl font-bold mb-4 text-gray-900">5. Your Rights</h3>
                <p className="mb-4 text-gray-700">
                  You have the right to:
                </p>
                <ul className="list-disc list-inside mb-6 text-gray-700 space-y-2">
                  <li>Access and review your personal information</li>
                  <li>Request corrections to inaccurate information</li>
                  <li>Opt out of certain communications</li>
                  <li>Request deletion of your information (subject to legal requirements)</li>
                </ul>

                <h3 className="text-2xl font-bold mb-4 text-gray-900">6. Cookies and Tracking</h3>
                <p className="mb-6 text-gray-700">
                  We use cookies and similar technologies to improve your experience on our website, analyze usage patterns, and provide personalized content. You can control cookie settings through your browser preferences.
                </p>

                <h3 className="text-2xl font-bold mb-4 text-gray-900">7. Third-Party Links</h3>
                <p className="mb-6 text-gray-700">
                  Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites and encourage you to review their privacy policies.
                </p>

                <h3 className="text-2xl font-bold mb-4 text-gray-900">8. Changes to This Policy</h3>
                <p className="mb-6 text-gray-700">
                  We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on our website and updating the effective date.
                </p>

                <h3 className="text-2xl font-bold mb-4 text-gray-900">9. Contact Us</h3>
                <p className="mb-4 text-gray-700">
                  If you have questions about this Privacy Policy or our privacy practices, please contact us:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700">
                    <strong>QuickLoanPro</strong><br />
                    4901 Bloomfield St<br />
                    New Orleans, LA 70121<br />
                    Phone: +1 (504) 438-1753<br />
                    Email: info@quickloanpro.com
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4">QuickLoanPro</h3>
              <p className="text-gray-400 mb-4">
                Your trusted partner for loans and financial assistance across Louisiana.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>+1 (504) 438-1753</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>info@quickloanpro.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>4901 Bloomfield St, New Orleans, LA 70121</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Loan Services</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/payday-loans" className="hover:text-white">Payday Loans</Link></li>
                <li><Link to="/personal-loans" className="hover:text-white">Personal Loans</Link></li>
                <li><Link to="/installment-loans" className="hover:text-white">Installment Loans</Link></li>
                <li><Link to="/cash-advances" className="hover:text-white">Cash Advances</Link></li>
                <li><Link to="/title-loans" className="hover:text-white">Title Loans</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/how-it-works" className="hover:text-white">How It Works</Link></li>
                <li><Link to="/loan-calculator" className="hover:text-white">Loan Calculator</Link></li>
                <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
                <li><Link to="/responsible-lending" className="hover:text-white">Responsible Lending</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/about" className="hover:text-white">About Us</Link></li>
                <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 QuickLoanPro. All rights reserved.</p>
            <p className="mt-2">Licensed financial service provider committed to responsible lending practices.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;